/**
 * AB实验主页面
 */
import React, { useEffect, useMemo, useState } from 'react';
import { connect, useIntl } from 'umi';
import classNames from 'classnames';
import { Button, Layout } from 'tea-component';
import { buriedPoint } from '@home/utils/buriedPoint';
import { LANGUAGE_EN } from '@home/_config/config';
import { getName } from '@home/utils/common';

import GlobalFilter from './components/GlobalFilter';
import EventFilter from './components/EventFilter';
import ExperimentCascade from './components/ExperimentCascade';
import MetricFilter from './components/MetricFilter';
import DimensionFilter from './components/DimensionFilter';
import ObservationSettings from './components/ObservationSettings';
import StateContent from './components/StateContent';
import DataTable from './components/DataTable';
import MetricChart from './components/MetricChart';
import AnalysisFooter from './components/AnalysisFooter';
import UserType from './components/UserType';
import DateRange from './components/DateRange';
import CountLimit from './components/CountLimit';
import { AnalysisCalculateHint } from '@/pages/datalab/pages/BehaviorAnalysis/ExplorationAnalysis/components/analysis_calculate_hint';
import { SaveModel } from './components/SaveModel';
import { CustomPrompt } from '@/pages/datalab/components/CustomPrompt';
import ConfirmModal from '@/pages/aigc/pages/Text2SQL/components/RelevantTable/components/ConfirmModal';
import { DownLoadModalContent } from '@/pages/datalab/pages/Multidimensional/Analysis/components/AnalysisLineChart';
import { IABExperimentProps } from './types';
import { useAbExperimentActions } from './hooks/useAbExperimentActions';

import './index.scss';
const { Content } = Layout;

function ABExperiment(props: IABExperimentProps) {
  const { location, gameCode, dispatch } = props;
  const { formatMessage, locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  const [isCloseLeft, setIsCloseLeft] = useState(false);

  // 获取路由信息（需在 useAbExperimentActions 前，供编辑加载使用）
  const { isEdit, editMsg, pageName, taskId } = useMemo(() => {
    const query = location?.query || {};
    const { from, editMsg, taskId, name, nameCn } = query;
    const isEditVal = from === 'edit' || false;
    let editMsgObj: any = {};
    if (editMsg) {
      try {
        editMsgObj = JSON.parse(editMsg);
      } catch (error) {
        console.warn('editMsg 解析失败，回退为空对象:', error);
        editMsgObj= {};
      }
    }
    const pageNameVal = isEditVal
      ? getName(isEn, editMsg?.name || name, editMsg?.nameCn || nameCn)
      : formatMessage({ id: 'dbl_ab_experiment_title' });
    return {
      isEdit: isEditVal,
      editMsg: editMsgObj,
      pageName: pageNameVal,
      taskId,
    };
  }, [location?.query, isEn, formatMessage]);

  // 打点
  useEffect(() => {
    buriedPoint({
      pageId: isEdit ? '602402' : '602401',
      pageName: isEdit ? pageName : 'A/B Test Analysis',
      extInfo: isEdit ? `{"pageName": "${pageName}", "pageNameCn": "${editMsg?.nameCn}", "id": "${taskId}"}` : `{"pageName": "A/B Test Analysis", "pageNameCn": "A/B实验分析"}`,
      pagePath: '/Analysis/ABTest',
      uid: gameCode,
      typeId: "datalab_pc_v2",
      menuId: '0',
    });
  }, [gameCode, isEdit, pageName, editMsg?.nameCn, taskId]);

  // 左侧菜单展开收起
  useEffect(() => {
    dispatch({
      type: 'datalab/save',
      payload: {
        isExpendLeftMenu: false
      }
    });
    return () => {
      // 展开左侧目录
      dispatch({
        type: 'datalab/save',
        payload: {
          isExpendLeftMenu: true
        }
      });
    };
  }, []);

  // 表单、配置、事件操作均由 hook 内部维护与透出
  const {
    formState,
    updateFormState,
    handleUserTypeChange,
    tableData,
    tableLoading,
    hasCalculated,
    hashEqual,
    hashEqualForSave,
    skipNextLeavePrompt,
    isMaxWidth,
    isOverLimit,
    disableCalculateButton,
    showSaveModel,
    setShowSaveModel,
    detailData,
    onCalculate,
    onPreviewSQL,
    onDownload,
    onDownloadConfirm,
    onDownloadModalClose,
    downloadModalVisible,
    downloadRowLimitValue,
    setDownloadRowLimitValue,
    onClearAll,
    saveHandle,
    sqlPreviewLoading,
    allFilters,
    optionFilters,
    eventList,
    allMetrics,
    optionMetrics,
    optionCustomMetrics,
    allDimensions,
    optionDimensions,
    experimentFilters,
  } = useAbExperimentActions({
    gameCode,
    isEdit,
    taskId,
    formatMessage,
    isEn,
    pageName,
  });

  const {
    userType,
    dateRange,
    globalFilterValue,
    globalFilterLogic,
    eventFilterValue,
    eventFilterLogic,
    experimentFilterValue,
    metricFilterValue,
    dimensionFilterValue,
    observationDays,
    limit,
    chartConfig,
  } = formState;

  return (
    <Content className={classNames("dbl-ab-experiment-content",
      { 'dbl-ab-experiment-content-max': isMaxWidth }
    )}>
      <Content.Header
        className="dbl-header"
        title={pageName}
        operation={
          <Button type="primary" disabled={disableCalculateButton} onClick={() => { setShowSaveModel(true) }}>
            {formatMessage({ id: 'dbl_common_save' })}
          </Button>
        }
      />
      <Content.Body full className="dbl-ab-experiment-body">
        <div className={classNames('dbl-left-context', { 'dbl-left-context-close': isCloseLeft })}>
          <div
            className='dbl-expand-collapse-left'
            onClick={() => {
              setIsCloseLeft(true);
            }}
          >
            <div className='dbl-expand-collapse-init' />
          </div>
          <div className="dbl-left-context-inner">
            <div className="dbl-left-context-item-wrapper">
              <div className='dbl-left-context-item'>
                <UserType
                  title={formatMessage({ id: 'dbl_ab_experiment_user_type_title' })}
                  value={userType}
                  onChange={handleUserTypeChange}
                />
                <DateRange
                  title={formatMessage({ id: 'dbl_ab_experiment_date_range_title' })}
                  value={dateRange}
                  onChange={(value) => { updateFormState('dateRange', value) }}
                />
              </div>
              <GlobalFilter
                className="dbl-left-context-item"
                title={formatMessage({ id: 'dbl_ab_experiment_global_filter_title' })}
                gameCode={gameCode}
                value={globalFilterValue}
                logic={globalFilterLogic}
                onChange={(value) => { updateFormState('globalFilterValue', value) }}
                onLogicChange={(value) => { updateFormState('globalFilterLogic', value) }}
                allFilters={allFilters || []}
                optionFilters={optionFilters || []}
              />
              <EventFilter
                className="dbl-left-context-item"
                title={formatMessage({ id: 'dbl_ab_experiment_event_filter_title' })}
                eventList={eventList || []}
                optionCustomMetrics={optionCustomMetrics ?? []}
                value={eventFilterValue}
                logic={eventFilterLogic}
                displayConfig={{
                  eventOccurDisplay: 'count',
                  showHoursSinceRegister: userType === 'new_user',
                }}
                onLogicChange={(value) => { updateFormState('eventFilterLogic', value) }}
                onConfirm={(value) => { updateFormState('eventFilterValue', value) }}
              />
              <ExperimentCascade
                className="dbl-left-context-item"
                title={formatMessage({ id: 'dbl_ab_experiment_experiment_cascade_title' })}
                gameCode={gameCode}
                value={experimentFilterValue}
                onChange={(value) => updateFormState('experimentFilterValue', value)}
                experimentFilters={experimentFilters ?? []}
              />
              <MetricFilter
                className="dbl-left-context-item"
                title={formatMessage({ id: 'dbl_ab_experiment_metric_filter_title' })}
                required={true}
                eventList={eventList}
                value={metricFilterValue}
                allMetrics={allMetrics}
                optionMetrics={optionMetrics}
                allFilters={allFilters}
                optionFilters={optionFilters}
                optionCustomMetrics={optionCustomMetrics ?? []}
                onChange={(value) => { updateFormState('metricFilterValue', value) }}
              />
              <DimensionFilter
                className="dbl-left-context-item"
                title={formatMessage({ id: 'dbl_ab_experiment_dimension_filter_title' })}
                value={dimensionFilterValue}
                allDimensions={allDimensions}
                optionDimensions={optionDimensions}
                onChange={(value) => { updateFormState('dimensionFilterValue', value) }}
              />
              <ObservationSettings
                className="dbl-left-context-item"
                title={formatMessage({ id: 'dbl_ab_experiment_observation_days' })}
                required={true}
                value={observationDays}
                onChange={(value) => { updateFormState('observationDays', value) }}
              />
            </div>
          </div>
          <div className="dbl-left-context-footer">
            <AnalysisFooter
              analysisRunning={tableLoading}
              disableButton={disableCalculateButton}
              previewSqlLoading={sqlPreviewLoading}
              onCalculate={() => onCalculate(formState)}
              onPreviewSQL={onPreviewSQL}
              onClearAll={onClearAll}
              afterNode={<CountLimit
                value={limit}
                onChange={(value) => {
                  updateFormState('limit', value);
                }}
              />}
            />
          </div>
        </div>
        <div className={classNames('dbl-right-context', { 'dbl-right-context-close': isCloseLeft, 'dbl-right-context-hash-equal': !hashEqual })}>
          <AnalysisCalculateHint hashEqual={hashEqual} />
          <div className="dbl-right-context-inner">
            {isCloseLeft ? (
              <div
                className='dbl-expand-collapse-right'
                onClick={() => {
                  setIsCloseLeft(false);
                }}
              >
                <div className='dbl-expand-collapse-init' />
              </div>
            ) : (
              ''
            )}
            <StateContent
              isLoading={tableLoading}
              isEmpty={!tableData?.table_data?.length}
              hasCalculated={hasCalculated}
            >
              <MetricChart
                table={tableData}
                isEn={isEn}
                isTranspose={Boolean(chartConfig?.isTranspose)}
                selectedMetrics={chartConfig?.selectedMetrics || []}
                onSelectedMetricsChange={(value) => {
                  updateFormState('chartConfig', {
                    ...chartConfig,
                    selectedMetrics: value,
                  });
                }}
              />
              <DataTable
                table={tableData}
                chartConfig={chartConfig}
                onChartConfigChange={(next) => updateFormState('chartConfig', { ...chartConfig, ...next })}
                handleDownload={onDownload}
                limit={limit}
                isOverLimit={isOverLimit}
              />
            </StateContent>
          </div>
        </div>
      </Content.Body>
      <SaveModel
        className="dbl-ab-experiment-analysis-save-model"
        showSaveModel={showSaveModel}
        setShowSaveModel={setShowSaveModel}
        saveHandle={saveHandle}
        namespace=""
        dispatch={dispatch}
        data={{
          taskId,
          business_type: detailData?.business_type || 0,
          name: detailData?.name || '',
          nameCn: detailData?.name_cn || '',
          label: detailData?.label || '',
        }}
      />
      <CustomPrompt
        title={formatMessage({ id: 'dbl_ab_experiment_leave_info' })}
        message={formatMessage({ id: 'dbl_common_leave_message' }, { name: '' })}
        isPrompt={!hashEqualForSave && (metricFilterValue?.length !== 0) && !skipNextLeavePrompt}
      />
      <ConfirmModal
        visible={downloadModalVisible}
        click={onDownloadConfirm}
        clone={onDownloadModalClose}
        {...({
          caption: formatMessage({ id: 'dbl_chart_modal_download_set' }),
          size: 580,
        } as any)}
      >
        <DownLoadModalContent
          radioValue={downloadRowLimitValue}
          handleOnRadioChange={(value: number | string) =>
            setDownloadRowLimitValue(Number(value))
          }
          isRaw={false}
        />
      </ConfirmModal>
    </Content>
  );
}

export default connect((state: any) => {
  return {
    gameCode: state.datalab?.gameCode,
  };
})(ABExperiment);
